import {
  Button,
  IconButton,
  Popover,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { noop } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as SupportKeyIcon } from '@work4all/assets/icons/support-key.svg';

import { useDialogs } from '@work4all/components';
import { useEmailTemplates } from '@work4all/components/lib/hooks';

import { useUser } from '@work4all/data';
import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { Contact } from '@work4all/models/lib/Classes/Contact.entity';
import { Ticket } from '@work4all/models/lib/Classes/Ticket.entity';
import { EMailTemplateKind } from '@work4all/models/lib/Enums/EMailTemplateKind.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { ContactPickerField } from '../../../../../../../components/entity-picker/ContactPickerField';
import { EmailTemplatePickerField } from '../../../../../../../components/entity-picker/EmailTemplatePickerField';
import { WORK4ALL_CUSTOMER_ID } from '../../../../../../../config';
import { settings, useSetting } from '../../../../../../../settings';
import { MaskControllerProps } from '../../../../types';
import { EmailTemplaterProvider } from '../../../email/email-templater/EmailTemplaterProvider';
import {
  EmailMaskCustomParams,
  EmailOverlayController,
} from '../../../email/EmailOverlayController';

import {
  InternalRequest,
  RequestStatus,
  useServiceAccessApi,
} from './hooks/use-service-access-api';

export const serviceAccessStatusColors = {
  [RequestStatus.OPEN]: 'var(--brand02)',
  [RequestStatus.GRANTED]: 'var(--success)',
  [RequestStatus.DENIED]: 'var(--error)',
  [RequestStatus.EXPIRED]: 'var(--error)',
};

interface ServiceAccessControllerProps {
  ticket?: Ticket;
}

const APP_URL = 'https://app.work4all.de';

export const ServiceAccessController = ({
  ticket,
}: ServiceAccessControllerProps) => {
  const { t } = useTranslation();

  const supportTokenPopoverRef = useRef<HTMLButtonElement>(null);
  const popoverState = usePopoverState('bottom');

  const [contact, setContact] = useState<Contact | null>(ticket?.contact);
  const ticketAccessRequestTemplateId = useSetting(
    settings.ticketAccessRequestTemplateId()
  );

  const {
    createInternalRequest,
    getInternalRequestList,
    getLoginUrl,
    deleteInternalRequest,
  } = useServiceAccessApi();

  const [openRequests, setOpenRequests] = useState<InternalRequest[]>([]);
  const [grantedRequests, setGrantedRequests] = useState<InternalRequest[]>([]);
  const [deniedRequests, setDeniedRequests] = useState<InternalRequest[]>([]);
  const [expiredRequests, setExpiredRequests] = useState<InternalRequest[]>([]);
  const allRequests = [
    ...grantedRequests,
    ...openRequests,
    ...deniedRequests,
    ...expiredRequests,
  ];

  useEffect(() => {
    setContact(ticket?.contact);
  }, [ticket?.contact]);

  const refreshRequests = useCallback(() => {
    getInternalRequestList().then((requests) => {
      const allRequests = requests.filter((x) => x.ticketId === ticket?.id);
      setOpenRequests(
        allRequests.filter((x) => x.status === RequestStatus.OPEN)
      );
      setGrantedRequests(
        allRequests.filter((x) => x.status === RequestStatus.GRANTED)
      );
      setDeniedRequests(
        allRequests.filter((x) => x.status === RequestStatus.DENIED)
      );
      setExpiredRequests(
        allRequests.filter((x) => x.status === RequestStatus.EXPIRED)
      );
    });
  }, [getInternalRequestList, ticket?.id]);

  useEffect(() => {
    refreshRequests();
  }, [refreshRequests]);

  const emailTemplates = useEmailTemplates([
    EMailTemplateKind.KEINE,
    EMailTemplateKind.TICKET,
  ]);
  const emailTemplate = useMemo(
    () =>
      emailTemplates.find(
        (e) => e.id === ticketAccessRequestTemplateId.value
      ) || null,
    [emailTemplates, ticketAccessRequestTemplateId.value]
  );

  const user = useUser();
  const [loading, setLoading] = useState(false);

  const dialogs = useDialogs();

  const openEmailMask = useCallback(
    async (serviceAccessId: string) => {
      refreshRequests();

      const link = `${APP_URL}/service-access?id=${serviceAccessId}`;

      const processedMailTemplateArgs = JSON.stringify({
        project: ticket?.project,
        projectId: ticket?.projectId,
        textFieldCustom: [
          {
            key: 'serviceAccess',
            value: JSON.stringify({
              requestLink: `<a href="${link}">${link}</a>`,
            }),
          },
        ],
      });
      const emailProps: MaskControllerProps<EmailMaskCustomParams> = {
        entity: Entities.eMail,
        id: null,
        params: {
          processedMailTemplateArgs,
          basedon: 'EmailTemplate',
          emailTemplate: emailTemplate
            ? JSON.stringify(emailTemplate)
            : undefined,
          ticketTemplateContext: JSON.stringify(ticket),
        },
        template: {
          entity: Entities.ticket,
          ids: [ticket?.id],
        },
        onAfterQuickSave: closeDialog,
        onAfterSave: closeDialog,
        onDelete: closeDialog,
      };

      const dialog = dialogs.overlay(
        <EmailOverlayController {...emailProps} />
      );

      function closeDialog() {
        dialogs.close(dialog.id);
      }
      setLoading(false);
    },
    [dialogs, emailTemplate, refreshRequests, ticket]
  );

  const handleNewRequest = useCallback(async () => {
    setLoading(true);

    const serviceAccessId = await createInternalRequest(
      ticket?.id,
      ticket?.businessPartner?.data?.number,
      contact?.id
    );

    openEmailMask(serviceAccessId);
    refreshRequests();
  }, [
    createInternalRequest,
    ticket?.id,
    ticket?.businessPartner?.data?.number,
    contact?.id,
    openEmailMask,
    refreshRequests,
  ]);

  const handleDeleteRequest = useCallback(
    async (serviceAccessId: string) => {
      await deleteInternalRequest(serviceAccessId);
      refreshRequests();
    },
    [deleteInternalRequest, refreshRequests]
  );

  const handleServiceAccessLogin = useCallback(
    async (serviceAccessId: string) => {
      const loginUrl = await getLoginUrl(serviceAccessId);

      if (loginUrl) {
        window.open(loginUrl, '_blank');
      }
    },
    [getLoginUrl]
  );

  const ctaColor = useMemo(() => {
    if (grantedRequests.length > 0)
      return serviceAccessStatusColors[RequestStatus.GRANTED];
    if (openRequests.length > 0)
      return serviceAccessStatusColors[RequestStatus.OPEN];
    if (deniedRequests.length > 0)
      return serviceAccessStatusColors[RequestStatus.DENIED];
    if (expiredRequests.length > 0)
      return serviceAccessStatusColors[RequestStatus.EXPIRED];
    return undefined;
  }, [grantedRequests, openRequests, deniedRequests, expiredRequests]);

  if (!ticket?.id || user.kundennummer !== WORK4ALL_CUSTOMER_ID) return null;

  return (
    <EmailTemplaterProvider
      loading={false}
      isCreateMode={false}
      state={{
        initialTemplate: emailTemplate,
        isDisabled: false,
        isLoading: false,
        setLoading: noop,
        template: emailTemplate,
        setTemplate: noop,
        signature: null,
        setSignature: noop,
        variables: null,
      }}
      tfm={null}
      previousEMail={null}
      emailMode={null}
    >
      <IconButton
        ref={supportTokenPopoverRef}
        color="primary"
        onClick={() => {
          popoverState.handleClick();
        }}
      >
        <SupportKeyIcon color={ctaColor} />
      </IconButton>
      <Popover {...popoverState} anchorEl={supportTokenPopoverRef.current}>
        <Stack p="1rem" width="20rem" gap="1rem">
          <Typography variant="h4">
            {t('TICKET.SERVICE_ACCESS_CONTROLLER.TITLE')}
          </Typography>
          {allRequests.length > 0 && (
            <Stack gap="1rem">
              {allRequests.map((req) => {
                return (
                  <>
                    <Stack
                      key={req.id}
                      direction="row"
                      alignItems="center"
                      gap="0.5rem"
                    >
                      <Skeleton
                        variant="circular"
                        width="1rem"
                        height="1rem"
                        style={{
                          background: serviceAccessStatusColors[req.status],
                        }}
                      />
                      <Typography variant="body1" flex={1}>
                        {t(
                          `TICKET.SERVICE_ACCESS_CONTROLLER.STATUS.${req.status}`
                        )}
                      </Typography>
                      {req.status === RequestStatus.GRANTED && (
                        <Button
                          onClick={() => handleServiceAccessLogin(req.id)}
                          startIcon={<SupportKeyIcon />}
                        >
                          {t('TICKET.SERVICE_ACCESS_CONTROLLER.ACCESS')}
                        </Button>
                      )}
                    </Stack>
                    {req.status === RequestStatus.GRANTED && (
                      <Typography variant="caption">
                        {t('TICKET.SERVICE_ACCESS_CONTROLLER.TUTORIAL')}
                      </Typography>
                    )}

                    {req.status === RequestStatus.OPEN && (
                      <Button onClick={() => openEmailMask(req.id)}>
                        {t('TICKET.SERVICE_ACCESS_CONTROLLER.SEND_AGAIN')}
                      </Button>
                    )}

                    {[RequestStatus.DENIED, RequestStatus.EXPIRED].includes(
                      req.status
                    ) && (
                      <Button
                        variant="contained"
                        onClick={() => handleDeleteRequest(req.id)}
                      >
                        {t('TICKET.SERVICE_ACCESS_CONTROLLER.NEW_REQUEST')}
                      </Button>
                    )}
                  </>
                );
              })}
            </Stack>
          )}
          {allRequests.length === 0 && (
            <>
              <ContactPickerField
                clearable={false}
                businessPartnerId={ticket?.businessPartnerId}
                businessPartnerType={ticket?.businessPartnerType}
                value={contact}
                onChange={(e) => setContact(e)}
                disabled={loading}
              />
              <EmailTemplatePickerField
                value={emailTemplate}
                onChange={(e) => e && ticketAccessRequestTemplateId.set(e.id)}
                eMailTemplateKind={EMailTemplateKind.TICKET}
                initialTemplateKind={EMailTemplateKind.TICKET}
                clearable={false}
                disabled={loading}
                required
              />
              <Typography variant="caption">
                {t('TICKET.SERVICE_ACCESS_CONTROLLER.DESCRIPTION')}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                disabled={!contact || !emailTemplate || loading}
                onClick={handleNewRequest}
              >
                {t('TICKET.SERVICE_ACCESS_CONTROLLER.CTA')}
              </Button>
            </>
          )}
        </Stack>
      </Popover>
    </EmailTemplaterProvider>
  );
};
